
import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { RequestService } from '@services/request.service';
import { PanelComponent } from '@components/core-template/panel/panel.component';

@Component({
  selector: 'app-permissions',
  templateUrl: './permissions.component.html',
  styleUrls: ["./permissions.component.css"],
  imports: [PanelComponent, FormsModule, ReactiveFormsModule, NgbTooltipModule],
  providers: [RequestService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionsComponent implements OnInit {

  private _requestService = inject(RequestService)
  private _fb = inject(FormBuilder)
  private _modalService = inject(NgbModal)
  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)

  protected matrixRoles: any
  protected matrixData: any
  protected filterType = 'backend'
  protected stateRules: any[] = []
  protected OPERATORS = [
    { value: 'eq', label: '=' },
    { value: 'neq', label: '!=' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '>=' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '<=' },
    { value: 'in', label: 'EN (lista separada por comas)' },
    { value: 'notIn', label: 'NO EN (lista separada por comas)' },
    { value: 'isNull', label: 'ES NULO' },
    { value: 'isNotNull', label: 'NO ES NULO' },
  ]
  protected ENTITY_TYPES = ['Request', 'PolicyCharge', 'Insured']

  protected permissionForm = this._fb.group({
    _id: [null],
    name: ["", Validators.required],
    type: ["backend", Validators.required],
    levelMin: [1, [Validators.required, Validators.min(1)]],
    levelMax: [5, [Validators.required, Validators.min(1)]],
    enabled: [true],
  })

  get filteredMatrix() {
    if (!this.matrixData) return []
    return this.matrixData.filter((row: any) => row.permission.type === this.filterType)
  }

  ngOnInit(): void {
    this.loadMatrix()
  }

  loadMatrix() {
    this._requestService.getMatrix().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.matrixRoles = data.roles
      this.matrixData = data.matrix
      this.cdr.markForCheck()
    })
  }

  toggleMatrix(permissionId: string, roleId: string, enabled: boolean) {
    const row = this.matrixData.find((r: any) => r.permission._id === permissionId)
    if (row) row.roles[roleId] = enabled
    this._requestService.toggleMatrix(roleId, permissionId, enabled).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.Toast.fire({ title: 'Permiso actualizado', icon: 'success' })
    })
  }

  openPermissionModal(modal: any, permission?: any) {
    this.permissionForm.reset()
    this.permissionForm.patchValue({ enabled: true, type: this.filterType, levelMin: 1, levelMax: 5 })
    this.stateRules = []
    if (permission) {
      this.permissionForm.patchValue(permission)
      this._requestService.getStateRules(permission._id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(rules => {
        this.stateRules = rules
        this.cdr.markForCheck()
      })
    }
    this._modalService.open(modal, { size: 'xl', scrollable: true, animation: true, backdrop: true }).result.then(() => {}, () => {})
  }

  savePermission(modal: any) {
    const obs = this.permissionForm.value._id
      ? this._requestService.updatePermission(this.permissionForm.value._id, this.permissionForm.value)
      : this._requestService.createPermission(this.permissionForm.value)
    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadMatrix()
      modal.close()
      this.Toast.fire({ title: this.permissionForm.value._id ? 'Permiso actualizado' : 'Permiso creado', icon: 'success' })
    })
  }

  addStateRule() {
    const pid = this.permissionForm.value._id
    if (!pid) {
      this.Toast.fire({ title: 'Guarda el permiso primero', icon: 'warning' })
      return
    }
    this._requestService.saveStateRule({
      permissionId: pid,
      entityName: 'Request',
      fieldPath: 'movement.priority',
      operator: 'eq',
      value: '1',
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(rule => {
      this.stateRules.push(rule)
      this.Toast.fire({ title: 'Regla agregada', icon: 'success' })
    })
  }

  removeStateRule(rule: any) {
    this._requestService.deleteStateRule(rule._id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.stateRules = this.stateRules.filter(r => r._id !== rule._id)
      this.Toast.fire({ title: 'Regla eliminada', icon: 'success' })
      this.cdr.markForCheck()
    })
  }

  saveStateRule(rule: any) {
    this._requestService.saveStateRule(rule).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.Toast.fire({ title: 'Regla actualizada', icon: 'success' })
    })
  }

  getOperatorLabel(value: string): string {
    return this.OPERATORS.find(o => o.value === value)?.label || value
  }

  public Toast = Swal.mixin({
    width: 500,
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true
  })
}
